import { hexToRgb, normalizeHex6 } from './hex';
import { resolveLabelColor } from './resolve';

export type LabelChipTheme = 'light' | 'dark';

export interface LabelChipStyles {
  backgroundColor: string;
  color: string;
  borderColor?: string;
}

export interface LabelChipOptions {
  /**
   * Tint strength of the chip fill (0–1). Defaults to the standard chip tint for the theme.
   * The border stays a fixed step stronger than the fill.
   */
  tintAlpha?: number;
}

/** Standard chip fill alpha per theme. */
export const CHIP_TINT_ALPHA: Record<LabelChipTheme, number> = { light: 0.14, dark: 0.22 };

/**
 * Chip text is always the theme foreground. The color lives in the fill and border only, so
 * labels meet AA on every chip tint and match the status pills.
 */
const CHIP_FOREGROUND: Record<LabelChipTheme, string> = {
  light: '#0A0301',
  dark: '#F4F0EA',
};

const BORDER_STEP: Record<LabelChipTheme, number> = { light: 0.14, dark: 0.16 };

/**
 * Derive muted chip styles from a base hex for light or dark theme.
 */
export function chipStylesFromHex(
  hex: string,
  theme: LabelChipTheme,
  options: LabelChipOptions = {}
): LabelChipStyles {
  const normalized = normalizeHex6(hex) ?? resolveLabelColor(hex);
  const { r, g, b } = hexToRgb(normalized);
  const tintAlpha = options.tintAlpha ?? CHIP_TINT_ALPHA[theme];
  const borderAlpha =
    options.tintAlpha === undefined
      ? theme === 'light'
        ? 0.28
        : 0.38
      : Math.min(1, tintAlpha + BORDER_STEP[theme]);

  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${tintAlpha})`,
    color: CHIP_FOREGROUND[theme],
    borderColor: `rgba(${r}, ${g}, ${b}, ${borderAlpha})`,
  };
}

/**
 * Resolve stored color (name or hex) and return chip inline styles.
 */
export function labelChipStyles(
  storedColor: string | null | undefined,
  theme: LabelChipTheme,
  options?: LabelChipOptions
): LabelChipStyles {
  return chipStylesFromHex(resolveLabelColor(storedColor), theme, options);
}
